import { useEffect, useMemo, useState } from "react";
import { useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Cloud, Save } from "lucide-react";
import {
  partnersApi,
  PARTNER_CATEGORY_LABEL,
  SETTLEMENT_MODE_LABEL,
  SETTLEMENT_CYCLE_LABEL,
  type PartnerCreate,
  type PartnerCategory,
  type SettlementMode,
  type SettlementCycle,
} from "@/services/b2b";
import { PageShell, Pill } from "@/shared/ui/PageHeader";
import { WizardToolbar } from "@/shared/wizard/WizardToolbar";
import { useWizardDraft } from "@/shared/wizardDraft/useWizardDraft";   // WIZDRAFT-2 · 服务器草稿（替代原来的本机草稿）

/**
 * 新建 B2B 客户向导 — 对齐 sign-off canvas「新建 B2B 客户向导」(铁律十九)。
 * 5 Step：公司信息 → 联系人 → 分级 & 信用 → API 对接 → 客户经理 + 确认。
 * 分级 VIP/普通/试用 按信用额度自动建议（与客户看板派生一致）· 信用 ≥ $5k 提示走老板审批。
 */
const STEPS = ["公司信息", "联系人", "分级 & 信用", "API 对接", "客户经理 + 确认"] as const;

/** WIZDRAFT-2 · 密钥 / 令牌 / 密码类字段不存进草稿（还原后要重新填）。本向导目前没有这类字段（第 4 步「API 对接」只有「不需要 API」一个勾选，凭证在 API 集成中心填），
 *  这里照工单兜底：以后向导里若加了名字带 secret / token / key / password / credential 的字段，自动不进草稿。 */
const SECRET_KEY = /(secret|token|api[_-]?key|password|passwd|credential)/i;
export function stripSecrets<T extends Record<string, unknown>>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([k]) => !SECRET_KEY.test(k))) as T;
}

function tierOf(cents: number): { label: string; cls: string } {
  if (cents >= 50_000_00) return { label: "VIP", cls: "bg-violet-100 text-violet-700" };
  if (cents >= 10_000_00) return { label: "普通", cls: "bg-blue-100 text-blue-700" };
  return { label: "试用", cls: "bg-slate-100 text-slate-600" };
}

export function ChannelPartnerWizardPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editing = !!id;
  const [step, setStep] = useState(0);
  const [noApi, setNoApi] = useState(true);
  const [form, setForm] = useState<PartnerCreate>({
    name: "", contact_name: "", contact_email: "", contact_phone: "",
    category: "traditional_agent", settlement_mode: "credit_monthly", settlement_cycle: "monthly", credit_limit_cents: 100_000,
    tax_id: "", billing_address: "", contract_start_date: "",
  });

  // 编辑模式：加载现有客户并预填表单
  const partnerQ = useQuery({ queryKey: ["b2b", "partner", id], queryFn: () => partnersApi.get(id!), enabled: editing });
  useEffect(() => {
    if (editing && partnerQ.data) {
      const p = partnerQ.data;
      setForm({
        name: p.name, contact_name: p.contact_name, contact_email: p.contact_email, contact_phone: p.contact_phone ?? "",
        category: p.category, settlement_mode: p.settlement_mode, settlement_cycle: p.settlement_cycle, credit_limit_cents: p.credit_limit_cents,
        notes: p.notes ?? "", tax_id: p.tax_id ?? "", billing_address: p.billing_address ?? "", contract_start_date: p.contract_start_date ?? "",
      });
      setNoApi(!p.api_enabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, partnerQ.data]);

  // WIZDRAFT-2 · 服务器草稿（只用于新建；编辑模式不存 / 不还原，避免污染别的客户数据）：每 30 秒 + 换步骤自动存，?draft=<id> 重开还原到存的那一步
  const [leaving, setLeaving] = useState(false);
  const draftPayload = useMemo(() => ({ form: stripSecrets(form as unknown as Record<string, unknown>), noApi }), [form, noApi]);
  const draft = useWizardDraft({
    kind: "channel_partner", enabled: !editing, step: step + 1, title: form.name.trim() || null, payload: draftPayload,
    onRestore: (d) => {
      const p = d.payload as { form?: Partial<PartnerCreate>; noApi?: boolean };
      if (p.form) setForm((f) => ({ ...f, ...stripSecrets(p.form as Record<string, unknown>) }));
      if (typeof p.noApi === "boolean") setNoApi(p.noApi);
      setStep(Math.min(Math.max(d.step - 1, 0), STEPS.length - 1));
    },
  });

  const update = (patch: Partial<PartnerCreate>) => setForm((f) => ({ ...f, ...patch }));
  const tier = tierOf(form.credit_limit_cents);
  const needsApproval = form.credit_limit_cents >= 5_000_00;

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const save = useMutation({
    mutationFn: async (payload: PartnerCreate & { reason_code?: string; reason?: string }) => {
      if (editing) return partnersApi.update(id!, payload);   // payload 已带 reason_*（改额度时 onClick 里先问过）
      draft.suspend();   // 点创建：暂停自动存（在途保存不用等：晚到的 PUT 404 不重建、晚到的 POST 自己删掉），带上草稿 id（建成功后草稿已被后端同事务删掉，不能再存回去）
      return partnersApi.create({ ...payload, ...(draft.currentId() ? { wizard_draft_id: draft.currentId()! } : {}) });
    },
    onSuccess: (p) => { navigate(`/admin/channel-partners/${editing ? id : p.id}`, { replace: true }); },
    onError: () => { if (!editing) draft.resume(); },   // 建失败（422 等）：草稿还在，恢复自动存
  });

  return (
    <PageShell
      title={editing ? `✏️ 编辑 B2B 客户${form.name ? ` · ${form.name}` : ""}` : "➕ 新建 B2B 客户向导"}
      subtitle={editing ? "修改客户信息 · 5 Step 同新建 · 保存后立即生效" : "5 Step · 公司 → 联系人 → 分级 → API → 客户经理 · 完成后通知老板审批信用额度"}
    >
      {!editing && (
        <WizardToolbar module="channel_partners" onApplyTemplate={(d) => update(d as Partial<PartnerCreate>)} />
      )}

      {/* stepper */}
      <div className="flex flex-wrap items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>
              {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className={`text-[12px] ${i === step ? "font-semibold text-slate-900" : "text-slate-500"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="h-px w-6 bg-slate-200" />}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        {step === 0 && (
          <div className="flex flex-col gap-4">
            <div className="text-[13px] text-slate-500">必填字段标 * · 税号用于开发票 · 注册地决定时区默认</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="公司名 *" value={form.name} onChange={(v) => update({ name: v })} />
              <Field label="税号 / EIN *" value={form.tax_id ?? ""} onChange={(v) => update({ tax_id: v })} />
              <Field label="注册地 *" value={form.billing_address ?? ""} onChange={(v) => update({ billing_address: v })} />
              <Field label="入网日期 *" type="date" value={form.contract_start_date ?? ""} onChange={(v) => update({ contract_start_date: v })} />
            </div>
            <div>
              <div className="mb-1.5 text-[12px] font-medium text-slate-700">业务类型</div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {(Object.keys(PARTNER_CATEGORY_LABEL) as PartnerCategory[]).map((c) => (
                  <button key={c} type="button" onClick={() => update({ category: c })}
                    className={`rounded-lg border px-3 py-2 text-[13px] ${form.category === c ? "border-blue-500 bg-blue-50 font-semibold text-blue-700" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>
                    {PARTNER_CATEGORY_LABEL[c]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="flex flex-col gap-4">
            <div className="text-[13px] text-slate-500">至少 1 主联系人 · 主联系人为日常对接人</div>
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="mb-2 text-[13px] font-semibold text-slate-800">👤 主联系人 *</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="姓名 *" value={form.contact_name} onChange={(v) => update({ contact_name: v })} />
                <Field label="电话 *" value={form.contact_phone ?? ""} onChange={(v) => update({ contact_phone: v })} />
                <Field label="邮箱 *" type="email" value={form.contact_email} onChange={(v) => update({ contact_email: v })} />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-4">
            <div className="text-[13px] text-slate-500">系统按信用额度自动建议分级 · 信用额度走老板审批</div>
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <span className="text-[13px] text-slate-600">系统建议分级：</span>
              <span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${tier.cls}`}>{tier.label}</span>
              <span className="text-[11px] text-slate-400">（按信用额度档：≥$50k VIP · ≥$10k 普通 · 否则 试用）</span>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="初始信用额度（美元）*" type="number" value={String(form.credit_limit_cents / 100)} onChange={(v) => update({ credit_limit_cents: Math.round(Number(v) * 100) || 0 })} />
              <div>
                <div className="mb-1.5 text-[12px] font-medium text-slate-700">结算周期 *</div>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.keys(SETTLEMENT_MODE_LABEL) as SettlementMode[]).map((m) => (
                    <button key={m} type="button" onClick={() => update({ settlement_mode: m })}
                      className={`rounded-lg border px-2 py-2 text-[12px] ${form.settlement_mode === m ? "border-blue-500 bg-blue-50 font-semibold text-blue-700" : "border-slate-200 bg-white text-slate-700"}`}>
                      {SETTLEMENT_MODE_LABEL[m]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                {/* B2B-D · 出账周期：决定同行站「对账单」页看到月结视图还是单结视图（同行侧不可切换，
                    见 tickets/ticket-B2B-D）。与上面"结算周期"(settlement_mode，预付/授信) 是不同的轴。 */}
                <div className="mb-1.5 text-[12px] font-medium text-slate-700">出账周期 *</div>
                <select
                  value={form.settlement_cycle ?? "monthly"}
                  onChange={(e) => update({ settlement_cycle: e.target.value as SettlementCycle })}
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-[12px] text-slate-700"
                >
                  <option value="monthly">{SETTLEMENT_CYCLE_LABEL.monthly}</option>
                  <option value="per_order">{SETTLEMENT_CYCLE_LABEL.per_order}</option>
                </select>
                <p className="mt-1 text-[11px] text-slate-400">决定同行站「对账单」页视图：月结=期间汇总 / 单结=每单单独出账</p>
              </div>
            </div>
            {needsApproval && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">💡 信用额度 ≥ $5k 必经老板审批后才生效 · 试用客户默认预付不需审</div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col gap-4">
            <div className="text-[13px] text-slate-500">API 对接独立配置 · 跳 API 集成中心 · mirror 模式</div>
            <label className="flex items-center gap-2 text-[13px] text-slate-700">
              <input type="checkbox" checked={noApi} onChange={(e) => setNoApi(e.target.checked)} className="h-4 w-4" />
              不需要 API · 客户经理手工下单（试用客户常见）
            </label>
            {!noApi && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-[12px] text-slate-500">
                API 配置项（订单 webhook URL / 凭证 callback URL）在 <b>API 集成中心</b> 填写 · 此处 mirror。
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="flex flex-col gap-2 text-[13px]">
            <div className="mb-1 text-[13px] font-semibold text-slate-800">确认信息</div>
            <Row k="公司名" v={form.name} />
            <Row k="税号 / 注册地" v={`${form.tax_id || "—"} · ${form.billing_address || "—"}`} />
            <Row k="业务类型" v={PARTNER_CATEGORY_LABEL[form.category]} />
            <Row k="主联系人" v={`${form.contact_name} · ${form.contact_email} · ${form.contact_phone || "—"}`} />
            <Row k="分级（建议）" v={tier.label} />
            <Row k="信用额度 / 结算" v={`$${(form.credit_limit_cents / 100).toLocaleString()} · ${SETTLEMENT_MODE_LABEL[form.settlement_mode]}`} />
            <Row k="出账周期" v={SETTLEMENT_CYCLE_LABEL[form.settlement_cycle ?? "monthly"]} />
            <Row k="API 对接" v={noApi ? "不接入（手工下单）" : "接入（API 集成中心配置）"} />
            <div className="mt-2 text-[11px] text-slate-500">{needsApproval ? "🔴 信用额度 ≥ $5k · 创建后通知老板审批信用额度" : "试用客户预付 · 无需老板审批"}</div>
            {save.error && <Pill tone="danger">{(save.error as Error).message || (editing ? "保存失败" : "创建失败")}</Pill>}
          </div>
        )}
      </div>

      {reasonDialog}
      {!editing && draft.restoreMsg && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="wizard-draft-restore-msg">{draft.restoreMsg}</div>}
      {!editing && !draft.ready && <div className="text-[12px] text-slate-400" data-testid="wizard-draft-loading">正在还原草稿…</div>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 text-[13px] disabled:opacity-50">
          <ArrowLeft className="h-3.5 w-3.5" /> 上一步
        </button>
        {/* WIZDRAFT-2 · 草稿状态小字：已自动存 HH:MM / 失败红字（不挡操作，30 秒后自动重试）+「存草稿并离开」 */}
        {!editing && draft.status.kind === "saved" && (
          <span className="inline-flex items-center gap-1 text-[12px] text-slate-400" data-testid="wizard-draft-status">
            <Cloud size={13} /> 已自动存草稿 {String(draft.status.at.getHours()).padStart(2, "0")}:{String(draft.status.at.getMinutes()).padStart(2, "0")}
          </span>
        )}
        {!editing && draft.status.kind === "error" && <span className="text-[12px] font-medium text-rose-600" role="alert" data-testid="wizard-draft-error">草稿没存上，稍后自动重试</span>}
        {!editing && (
          <button type="button" data-testid="wizard-draft-leave" disabled={!draft.ready || leaving || save.isPending}
            onClick={async () => { setLeaving(true); const ok = await draft.saveNow(); setLeaving(false); if (ok) navigate("/admin/channel-partners"); }}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 text-[13px] disabled:opacity-40">
            <Save className="h-3.5 w-3.5" /> {leaving ? "保存中…" : "存草稿并离开"}
          </button>
        )}
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={() => setStep((s) => s + 1)} className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white">
            下一步 <ArrowRight className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button type="button" disabled={save.isPending} onClick={async () => {
            // REASON-1 · 编辑时授信额度变了 → 先问原因（后端 422 兜底）
            const old = partnerQ.data?.credit_limit_cents;
            if (editing && old != null && old !== form.credit_limit_cents) {
              const r = await askReason({
                scene: "credit_change", title: "调整授信额度", subtitle: form.name,
                changes: [{ label: "基础授信额度", before: `$${(old / 100).toLocaleString()}`, after: `$${(form.credit_limit_cents / 100).toLocaleString()}` }],
                confirmText: "确认并保存",
              });
              if (!r) return;
              save.mutate({ ...form, ...r });
              return;
            }
            save.mutate(form);
          }} className="flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50">
            {save.isPending ? "提交中..." : editing ? "保存修改" : "确认创建"}
          </button>
        )}
      </div>
    </PageShell>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] font-medium text-slate-700">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
    </label>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5 last:border-0">
      <span className="text-slate-500">{k}</span>
      <span className="font-medium text-slate-900">{v}</span>
    </div>
  );
}
